import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronLeft, ChevronRight, Youtube } from "lucide-react";
import { PRODUCT_SLIDES } from "@/data/productGuide";
import { YOUTUBE_VIDEOS, getYoutubeEmbedUrl } from "@/data/youtubeVideos";
import { PRODUCT_GUIDE } from "@/constants/testIds";

export default function ProductGuide() {
  const [index, setIndex] = useState(0);
  const slide = PRODUCT_SLIDES[index];

  const goTo = (i) => setIndex((i + PRODUCT_SLIDES.length) % PRODUCT_SLIDES.length);

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 py-10 space-y-6">
      <div className="space-y-2 text-center">
        <span className="inline-block text-xs uppercase tracking-[0.2em] font-semibold text-denso-red bg-red-50 px-3 py-1 rounded-full">
          Interactive Guide
        </span>
        <h1 className="font-heading text-3xl sm:text-4xl font-bold text-slate-900">DENSO Product Guide</h1>
        <p className="text-slate-600 text-base max-w-xl mx-auto">
          Browse through the DENSO product range at your own pace. No video required — just click
          through the presentation.
        </p>
      </div>

      <div className="relative bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
        <AnimatePresence mode="wait">
          <motion.div
            key={slide.id}
            data-testid={PRODUCT_GUIDE.slide(slide.id)}
            initial={{ opacity: 0, x: 30 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -30 }}
            transition={{ duration: 0.3 }}
            className="grid sm:grid-cols-2"
          >
            <div className="relative h-56 sm:h-full min-h-[280px]">
              <img src={slide.image} alt={slide.title} className="absolute inset-0 w-full h-full object-cover" />
              <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-black/0 to-transparent" />
              <div className="absolute bottom-4 left-4 right-4">
                <span className="text-white/90 text-xs uppercase tracking-widest font-semibold">
                  {index + 1} / {PRODUCT_SLIDES.length}
                </span>
              </div>
            </div>
            <div className="p-6 sm:p-8 space-y-4">
              <div>
                <h2 className="font-heading text-2xl font-bold text-slate-900">{slide.title}</h2>
                <p className="text-denso-red text-sm font-semibold mt-1">{slide.tagline}</p>
              </div>
              <ul className="space-y-2.5">
                {slide.points.map((p, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm text-slate-600 leading-relaxed">
                    <span className="h-1.5 w-1.5 rounded-full bg-denso-lightred mt-1.5 shrink-0" />
                    {p}
                  </li>
                ))}
              </ul>
              <div className="bg-red-50 border-l-2 border-denso-red rounded-r-md px-4 py-3">
                <p className="text-sm text-slate-700 italic leading-relaxed">{slide.sellingPoint}</p>
              </div>
            </div>
          </motion.div>
        </AnimatePresence>
      </div>

      <div className="flex items-center justify-between">
        <button
          onClick={() => goTo(index - 1)}
          data-testid={PRODUCT_GUIDE.prevButton}
          className="flex items-center gap-1.5 text-sm font-medium text-slate-600 hover:text-denso-red transition-colors duration-200 px-4 py-2 rounded-md hover:bg-red-50"
        >
          <ChevronLeft className="h-4 w-4" /> Previous
        </button>

        <div className="flex items-center gap-2">
          {PRODUCT_SLIDES.map((s, i) => (
            <button
              key={s.id}
              data-testid={PRODUCT_GUIDE.dot(i)}
              onClick={() => setIndex(i)}
              className={`h-2 rounded-full transition-all duration-200 ${
                i === index ? "w-6 bg-denso-red" : "w-2 bg-slate-300"
              }`}
            />
          ))}
        </div>

        <button
          onClick={() => goTo(index + 1)}
          data-testid={PRODUCT_GUIDE.nextButton}
          className="flex items-center gap-1.5 text-sm font-medium text-slate-600 hover:text-denso-red transition-colors duration-200 px-4 py-2 rounded-md hover:bg-red-50"
        >
          Next <ChevronRight className="h-4 w-4" />
        </button>
      </div>

      <div className="pt-8 space-y-4">
        <div className="space-y-2 text-center">
          <span className="inline-flex items-center gap-1.5 text-xs uppercase tracking-[0.2em] font-semibold text-denso-red bg-red-50 px-3 py-1 rounded-full">
            <Youtube className="h-3.5 w-3.5" /> Official DENSO Videos
          </span>
          <h2 className="font-heading text-2xl sm:text-3xl font-bold text-slate-900">Watch & Learn More</h2>
          <p className="text-slate-600 text-sm max-w-xl mx-auto">
            A curated selection of official DENSO videos covering products, technology and quality standards.
          </p>
        </div>

        <div className="grid sm:grid-cols-2 gap-5">
          {YOUTUBE_VIDEOS.map((video) => (
            <div
              key={video.id}
              data-testid={PRODUCT_GUIDE.youtubeCard(video.id)}
              className="bg-white rounded-md border border-slate-200 overflow-hidden transition-all duration-200 hover:-translate-y-1 hover:shadow-md"
            >
              <div className="relative aspect-video w-full bg-black">
                <iframe
                  data-testid={PRODUCT_GUIDE.youtubeIframe(video.id)}
                  src={getYoutubeEmbedUrl(video.id)}
                  title={video.title}
                  className="absolute inset-0 w-full h-full"
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                  allowFullScreen
                />
              </div>
              <div className="p-4 space-y-1">
                <h3 className="font-semibold text-slate-800 text-sm">{video.title}</h3>
                <p className="text-xs text-slate-500 leading-relaxed">{video.description}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
